import { useRef, type CSSProperties, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { animate } from 'motion';
import Icon from '../../ui/Icon/Icon';
import styles from './FlowingMenu.module.css';

export type FlowingMenuMedia =
    | { kind: 'icon' | 'window'; src: string }
    | { kind: 'glyph'; path: string };

export interface FlowingMenuEntry {
    label: string;
    media: FlowingMenuMedia;
}

export interface FlowingMenuItem {
    href: string;
    title: string;
    detail: string;
    entries: FlowingMenuEntry[];
}

type Edge = 'top' | 'bottom';

const REVEAL = { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const };
const HIDDEN_OFFSET = '101%';
const MARQUEE_REPEATS = 2;
const SECONDS_PER_ENTRY = 4;
const GLYPH_VIEW_BOX = '0 0 24 24';

function closestEdge(event: MouseEvent<HTMLElement>, element: HTMLElement): Edge {
    const bounds = element.getBoundingClientRect();
    return event.clientY - bounds.top < bounds.height / 2 ? 'top' : 'bottom';
}

function outsideOffset(edge: Edge): string {
    return edge === 'top' ? `-${HIDDEN_OFFSET}` : HIDDEN_OFFSET;
}

function insideOffset(edge: Edge): string {
    return edge === 'top' ? HIDDEN_OFFSET : `-${HIDDEN_OFFSET}`;
}

function EntryMedia({ media }: { media: FlowingMenuMedia }) {
    if (media.kind === 'glyph') {
        return (
            <svg className={styles.glyph} viewBox={GLYPH_VIEW_BOX} fill="currentColor" focusable="false">
                <path d={media.path} />
            </svg>
        );
    }
    return <img className={styles[media.kind]} src={media.src} alt="" loading="lazy" decoding="async" />;
}

function FlowingMenuRow({ item }: { item: FlowingMenuItem }) {
    const rowRef = useRef<HTMLLIElement>(null);
    const marqueeRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    const reveal = (event: MouseEvent<HTMLAnchorElement>) => {
        if (!rowRef.current || !marqueeRef.current || !innerRef.current) return;
        const edge = closestEdge(event, rowRef.current);
        animate(marqueeRef.current, { y: [outsideOffset(edge), '0%'] }, REVEAL);
        animate(innerRef.current, { y: [insideOffset(edge), '0%'] }, REVEAL);
    };

    const conceal = (event: MouseEvent<HTMLAnchorElement>) => {
        if (!rowRef.current || !marqueeRef.current || !innerRef.current) return;
        const edge = closestEdge(event, rowRef.current);
        animate(marqueeRef.current, { y: outsideOffset(edge) }, REVEAL);
        animate(innerRef.current, { y: insideOffset(edge) }, REVEAL);
    };

    const marqueeDuration = { '--marquee-duration': `${item.entries.length * SECONDS_PER_ENTRY}s` } as CSSProperties;

    return (
        <li ref={rowRef} className={styles.row}>
            <Link to={item.href} className={styles.link} onMouseEnter={reveal} onMouseLeave={conceal}>
                <span className={styles.title}>{item.title}</span>
                <span className={styles.detail}>{item.detail}</span>
                <span className={styles.arrow} aria-hidden="true">
                    <Icon name="arrowUpRight" />
                </span>
            </Link>
            <div ref={marqueeRef} className={styles.marquee} aria-hidden="true">
                <div ref={innerRef} className={styles.marqueeInner}>
                    <div className={styles.track} style={marqueeDuration}>
                        {Array.from({ length: MARQUEE_REPEATS }, (_, repeat) => (
                            <div key={repeat} className={styles.segment}>
                                {item.entries.map((entry) => (
                                    <span key={entry.label} className={styles.entry}>
                                        <EntryMedia media={entry.media} />
                                        <span className={styles.entryLabel}>{entry.label}</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </li>
    );
}

export default function FlowingMenu({ items }: { items: FlowingMenuItem[] }) {
    return (
        <ul className={styles.menu}>
            {items.map((item) => (
                <FlowingMenuRow key={item.title} item={item} />
            ))}
        </ul>
    );
}
